import { useContext, createContext, useState, useCallback, useEffect } from "react";
import { useAuth } from "./useAuth";

export const featuresContext = createContext();

export const DEFAULT_FEATURES = {
  "order-provider": {
    encounters: false,
    orders: true,
    ordersSalesDashboard: false,
    ordersManagement: false,
    uploadDocuments: true,
    createFollowUp: false,
    locations: false,
    icdCodes: false,
    analytics: false,
    team: true,
    cptCodes: false,
    levelOfServiceCodes: false,
    magicPasteCpt: false,
    magicPasteIcd10: false,
    homeHealth: false,
    transitionOfCare: false,
    woundCare: false,
    qCodes: false,
    managementGrid: false,
    peptides: true,
    newAccountForm: true,
  },
  "order-manufacturer": {
    encounters: false,
    orders: false,
    ordersSalesDashboard: true,
    ordersManagement: true,
    uploadDocuments: true,
    createFollowUp: false,
    locations: false,
    icdCodes: false,
    analytics: false,
    team: true,
    cptCodes: false,
    levelOfServiceCodes: false,
    magicPasteCpt: false,
    magicPasteIcd10: false,
    homeHealth: false,
    transitionOfCare: false,
    woundCare: false,
    qCodes: false,
    managementGrid: false,
    peptides: true,
    auditPacketWorkflow: true,
  },
  "order-patient": {
    encounters: false,
    orders: false,
    ordersSalesDashboard: false,
    ordersManagement: false,
    uploadDocuments: false,
    createFollowUp: false,
    locations: false,
    icdCodes: false,
    analytics: false,
    team: false,
    cptCodes: false,
    levelOfServiceCodes: false,
    magicPasteCpt: false,
    magicPasteIcd10: false,
    homeHealth: false,
    transitionOfCare: false,
    woundCare: false,
    qCodes: false,
    managementGrid: false,
    peptides: true,
    newAccountForm: false,
  },
  // The backend's free / unpaid tier: new signups start here and a cancelled
  // Stripe subscription falls back to it. The server caps it at
  // `basicPlanMaxEncounters` AI generations (User Plan / Check Plan processes),
  // so encounters stay reachable and the upgrade modal takes over at the cap.
  basic: {
    encounters: true,
    uploadDocuments: false,
    createFollowUp: false,
    locations: false,
    icdCodes: false,
    analytics: false,
    team: false,
    cptCodes: false,
    levelOfServiceCodes: false,
    magicPasteCpt: false,
    magicPasteIcd10: false,
    homeHealth: false,
    transitionOfCare: false,
    woundCare: false,
    qCodes: false,
    managementGrid: false,
  },
  premium: {
    encounters: true,
    uploadDocuments: false,
    createFollowUp: false,
    locations: false,
    icdCodes: false,
    analytics: false,
    team: false,
    cptCodes: false,
    levelOfServiceCodes: false,
    magicPasteCpt: false,
    magicPasteIcd10: false,
    homeHealth: false,
    transitionOfCare: false,
    woundCare: false,
    qCodes: false,
    managementGrid: false,
  },
  pro: {
    encounters: true,
    uploadDocuments: true,
    createFollowUp: true,
    locations: true,
    icdCodes: true,
    analytics: false,
    team: false,
    cptCodes: false,
    levelOfServiceCodes: false,
    magicPasteCpt: false,
    magicPasteIcd10: false,
    homeHealth: false,
    transitionOfCare: false,
    woundCare: false,
    qCodes: false,
    managementGrid: false,
  },
  ultimate: {
    encounters: true,
    orders: true,
    uploadDocuments: true,
    createFollowUp: true,
    locations: true,
    icdCodes: true,
    analytics: true,
    team: true,
    cptCodes: true,
    levelOfServiceCodes: true,
    magicPasteCpt: true,
    magicPasteIcd10: true,
    homeHealth: true,
    transitionOfCare: true,
    woundCare: true,
    qCodes: true,
    managementGrid: true,
  },
};

// The EHR plan-tier paywalls — and ONLY these — are what an admin session
// bypasses. This is deliberately an allow-list, not a deny-list: DEFAULT_FEATURES
// also holds role/shape discriminators (ordersManagement, ordersSalesDashboard,
// peptides, newAccountForm, auditPacketWorkflow) and org-config gates (orders,
// encounters) that decide which module a user lands in via computeHomeRoute.
// Flipping any of those would change an admin's role and routing rather than
// just unlocking a feature. An allow-list also fails closed when a new feature
// key is added to DEFAULT_FEATURES.
const ADMIN_BYPASSABLE_FEATURES = [
  "uploadDocuments",
  "createFollowUp",
  "locations",
  "icdCodes",
  "analytics",
  "team",
  "cptCodes",
  "levelOfServiceCodes",
  "magicPasteCpt",
  "magicPasteIcd10",
  "homeHealth",
  "transitionOfCare",
  "woundCare",
  "qCodes",
  "managementGrid",
];

const PREMIUM_NOTE_TYPES = {
  woundCare: ["woundcare-procedure", "woundcare-followup", "woundcare-home-visit", "woundcare-new"],
  homeHealth: ["oasis-pt-soc", "oasis-start"],
  transitionOfCare: ["transition-care"],
};

export const getNoteTypeFeature = (noteType) => {
  if (!noteType) return null;

  for (const [feature, noteTypes] of Object.entries(PREMIUM_NOTE_TYPES)) {
    if (noteTypes.includes(noteType)) {
      return feature;
    }
  }

  return null;
};

export const getUsedFeaturesFromChats = (chats) => {
  if (!chats || !Array.isArray(chats)) return [];

  const features = new Set();

  chats.forEach((chat) => {
    const noteType = chat.contextVariables?.["local:encounterType"];
    if (noteType) {
      const feature = getNoteTypeFeature(noteType);

      if (feature) {
        features.add(feature);
      }
    }
  });

  return Array.from(features);
};

function useFeaturesProvider() {
  // const { auth, fetchColumnsSmartSheet } = useAuth();
  const { auth, isAdminUser } = useAuth();
  const [requestedFeature, setRequestedFeature] = useState(null);
  const [showTierRecommendation, setShowTierRecommendation] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const getFeatureFlags = useCallback(() => auth?.brain?.variables ?? {}, [auth?.brain?.variables]);

  const hasFeature = useCallback(
    (_featureName) => {
      const featureName = _featureName;

      if (
        featureName === "debug" &&
        (auth?.session?.currentOrganizationName === "beta" || ["local", "staging"].includes(process.env.REACT_APP_ENV))
      ) {
        return true;
      }

      if (featureName === "orders") {
        if (auth?.orgInfo?.productLines?.dme === true || auth?.orgInfo?.productLines?.peptides === true) {
          return true;
        }
      }

      if (featureName === "encounters" && auth?.orgInfo?.productLines?.encounters !== undefined) {
        return auth?.orgInfo?.productLines?.encounters;
      }

      if (featureName === "insuranceVerification") {
        return auth?.orgInfo?.addOns?.insuranceVerification?.enabled === true;
      }

      // TEMPORARILY DISABLED — the clinic-inventory build is still in
      // progress (provider-side receipt UI, scan-to-receive, count audit
      // flow, end-to-end stock reconciliation against manufacturer ships).
      // Shipping a half-built tracking surface is worse than no surface
      // because providers would trust phantom on-hand counts.
      //
      // Manufacturer inventory + stock (CatalogManager bulk inventory,
      // master stock editor, per-variant catalog stock) is unaffected —
      // that ships through a separate code path (peptideCatalog /
      // peptideStock on the manufacturer org), not this feature flag.
      //
      // Mirrored across PR #2144 (the dedicated disable PR) and the
      // checkout-layout PR so the rollout doesn't depend on merge
      // ordering. To restore when the build is complete, replace the
      // `return false` below with the prior gating block preserved in
      // git history:
      //   if (auth?.orgInfo?.featureFlags?.trackPeptideInventory === true) return true;
      //   if (auth?.session?.currentOrganizationName === "beta") return true;
      //   if (["local", "staging"].includes(process.env.REACT_APP_ENV)) return true;
      //   return false;
      if (featureName === "trackPeptideInventory") {
        return false;
      }

      // Admin (Lucas super-administrator / impersonation) sessions bypass
      // PLAN-TIER paywalls only. This deliberately sits below every branch
      // above it: `encounters` and `orders` drive which module the user lands
      // in (see computeHomeRoute in src/Routes.js), and trackPeptideInventory
      // is disabled by design while the clinic-inventory build is incomplete.
      // Flipping those for admins would change routing and surface phantom
      // stock counts rather than just unlocking features. ADMIN_BYPASSABLE_FEATURES
      // is an explicit allow-list of plan-tier paywalls only — role/shape
      // discriminators in DEFAULT_FEATURES (ordersManagement, ordersSalesDashboard,
      // peptides, newAccountForm, auditPacketWorkflow) are deliberately excluded
      // regardless of how execution got here, so a new key added to
      // DEFAULT_FEATURES later does not silently become bypassable.
      if (isAdminUser && ADMIN_BYPASSABLE_FEATURES.includes(featureName)) return true;

      return DEFAULT_FEATURES[auth?.session?.currentOrganizationPlan]?.[featureName] ?? false;
    },
    // Note: `auth?.orgInfo?.featureFlags` is intentionally NOT in the deps
    // array right now — the trackPeptideInventory branch above is hard-
    // coded to `return false` so the only featureFlags read is dead. When
    // that gating is restored, add `auth?.orgInfo?.featureFlags` back here.
    [
      auth?.session?.currentOrganizationName,
      auth?.session?.currentOrganizationPlan,
      auth?.orgInfo?.productLines,
      auth?.orgInfo?.addOns,
      isAdminUser,
    ]
  );

  useEffect(() => {
    // Fetch SmartSheet columns
    // if (hasFeature("managementGrid")) {
    //   fetchColumnsSmartSheet();
    // }
    // eslint-disable-next-line
  }, [auth?.session]);

  const canUseFeature = useCallback(
    (featureName) => {
      const isAvailable = hasFeature(featureName);

      if (!isAvailable) {
        setRequestedFeature(featureName);
      }

      return isAvailable;
    },
    [hasFeature]
  );

  const canUseNoteType = useCallback(
    (noteType) => {
      const requiredFeature = getNoteTypeFeature(noteType);

      if (!requiredFeature) {
        return true;
      }

      return canUseFeature(requiredFeature);
    },
    [canUseFeature]
  );

  const closeUpgradeModal = useCallback(() => setRequestedFeature(null), []);

  const openTierRecommendation = useCallback(() => {
    setShowTierRecommendation(true);
    setRequestedFeature(null);
  }, []);

  const closeTierRecommendation = useCallback(() => {
    setShowTierRecommendation(false);
  }, []);

  return {
    hasFeature,
    canUseFeature,
    canUseNoteType,
    requestedFeature,
    closeUpgradeModal,
    showTierRecommendation,
    openTierRecommendation,
    closeTierRecommendation,
  };
}

export function ProvideFeatures({ children }) {
  const values = useFeaturesProvider();
  return <featuresContext.Provider value={values}>{children}</featuresContext.Provider>;
}

export const useFeatures = () => {
  return useContext(featuresContext);
};
